/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * Aui 컴포넌트 내부의 구성요소 클래스를 정의한다.
 *
 * @file /src/components/menu/elements/items/Button.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 6. 14.
 */
import * as parent from '../Item';
import { Properties as MenuItem } from '../Items';
import { Language, LanguageText } from '@utils/Language';
import { Menu } from '@components/menu/Menu';
import { Dom } from '@utils/Dom';
import { Html } from '@utils/Html';

export interface Listeners<T> extends parent.Listeners<T> {}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 메뉴타입
     */
    type: 'button';

    /**
     * 메뉴 아이콘
     */
    icon?: string;

    /**
     * 메뉴명
     */
    text?: LanguageText;

    /**
     * 서브메뉴아이템
     */
    menu?: Menu | MenuItem[];

    /**
     * 메뉴 숨김 여부
     */
    hidden?: boolean;

    /**
     * 메뉴 클릭 핸들러
     */
    handler?: (me: T) => Promise<boolean | void>;

    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export class Button extends parent.Item {
    /**
     * 메뉴아이템 타입
     */
    variant: string = 'button';

    /**
     * 메뉴명
     */
    text: LanguageText | null = null;

    /**
     * 메뉴 아이콘 스타일시트 클래스
     */
    icon: string | null = null;

    /**
     * 메뉴 숨김 여부
     */
    hidden: boolean = false;

    /**
     * 메뉴 클릭 핸들러
     */
    handler: (me: Button) => Promise<boolean | void> = null;

    /**
     * 메뉴 버튼 DOM
     */
    $button: Dom = null;

    /**
     * 아이콘 DOM
     */
    $icon: Dom = null;

    /**
     * 텍스트 DOM
     */
    $text: Dom = null;

    /**
     * 서브메뉴
     */
    menu: Menu = null;

    /**
     * 버튼 메뉴 아이템을 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties<Button>) {
        super(properties);

        this.text = this.properties.text ?? null;
        this.icon = this.properties.icon ?? null;
        this.hidden = this.properties.hidden === true;
        this.handler = this.properties.handler ?? null;
        this.menu = null;
    }

    /**
     * 부모 컴포넌트를 설정한다.
     *
     * @param component - 현재 엘리먼트를 가진 컴포넌트
     */
    setComponent(component: Menu): void {
        this.component = component;
        if ((this.properties.menu ?? null) !== null) {
            this.menu = component.createSubMenu(this.properties.menu);
        }
    }

    /**
     * 버튼 DOM 을 가져온다.
     *
     * @returns 버튼 DOM
     */
    $getButton(): Dom {
        if (this.$button === null) {
            this.$button = Html.create('button');
            this.$button.setAttr('type', 'button');
            this.$button.on('click', async (_, e: PointerEvent) => {
                if (this.menu !== null) {
                    this.menu.show();
                } else if (typeof this.handler == 'function') {
                    const hide = await this.handler(this);
                    if (hide !== false && this.getComponent().type === 'menu') {
                        (this.getComponent() as Menu).hide(true);
                    }
                }

                e.stopImmediatePropagation();
            });

            if (this.menu !== null) {
                this.menu.setParent(this.getComponent());
                this.menu.setTarget(this.$getElement(), 'x');
                this.menu.$getComponent().addClass('submenu');

                this.$getElement().on('mouseenter', async () => {
                    this.menu.show();
                });

                this.$getElement().on('mouseleave', async (_, e: MouseEvent) => {
                    const menuEl = this.menu.$getComponent().getEl();
                    const relatedTarget = e.relatedTarget as Node;

                    if (!menuEl.contains(relatedTarget)) {
                        this.menu.hide();
                    }
                });

                this.menu.$getComponent().on('mouseleave', (_, e: MouseEvent) => {
                    const itemEl = this.$getElement().getEl();
                    const relatedTarget = e.relatedTarget as Node;

                    if (!itemEl.contains(relatedTarget)) {
                        this.menu.hide();
                    }
                });
            }
        }

        return this.$button;
    }

    /**
     * 버튼라벨 DOM 을 가져온다.
     *
     * @returns 버튼라벨 DOM
     */
    $getText(): Dom {
        this.$text ??= Html.create('span');
        return this.$text;
    }

    /**
     * 아이콘 DOM 을 가져온다.
     *
     * @returns 아이콘 DOM
     */
    $getIcon(): Dom {
        if (this.$icon === null) {
            this.$icon = Html.icon(this.icon);
        }

        return this.$icon;
    }

    /**
     * 텍스트를 변경한다.
     *
     * @param text - 변경할 텍스트 (NULL 인 경우 텍스트를 제거한다.)
     */
    async setText(text: LanguageText | null = null): Promise<void> {
        this.text = text;

        const $text = this.$getText();
        $text.html(await Language.resolveText(this.text));
    }

    /**
     * 아이콘을 변경한다.
     *
     * @param icon - 변경할 아이콘 (NULL 인 경우 아이콘을 제거한다.)
     */
    setIcon(icon: string | null = null): void {
        this.icon = icon;
        Html.icon(this.icon, this.$getIcon());
    }

    /**
     * 메뉴 아이템에 포커스를 지정한다.
     */
    focus(): boolean {
        this.$getButton().focus();
        return true;
    }

    /**
     * 버튼의 로딩상태여부를 설정한다.
     *
     * @param loading - 로딩상태여부
     * @returns 메뉴아이템
     */
    setLoading(loading: boolean): this {
        if (loading == true) {
            this.$getButton().addClass('loading');
        } else {
            this.$getButton().removeClass('loading');
        }

        this.setDisabled(loading);
        return this;
    }

    /**
     * 버튼의 비활성화여부를 설정한다.
     *
     * @param disabled - 비활성화여부
     * @returns 메뉴아이템
     */
    setDisabled(disabled: boolean): this {
        this.$getButton().toggleAttr('disabled', disabled);
        return this;
    }

    /**
     * 엘리먼트를 렌더링한다.
     */
    async render(): Promise<void> {
        if (this.isRendered() == true) {
            return;
        }

        if (this.isHidden() == true) {
            this.hide();
        }

        if (this.icon !== null) {
            this.setIcon(this.icon);
        }
        this.$getButton().append(this.$getIcon());

        if (this.text !== null) {
            this.setText(this.text);
        }
        this.$getButton().append(this.$getText());

        if (this.menu !== null) {
            const $submenu = Html.create('aside');
            $submenu.addClass('mi', 'mi-caret-right');
            this.$getButton().append($submenu);
        }

        this.$getElement().append(this.$getButton());

        if (this.hidden == true) {
            this.$getElement().hide();
        } else {
            this.$getElement().show();
        }

        await this.onRender();
    }

    /**
     * 엘리먼트를 제거한다.
     *
     * @param removeDom - 엘리먼트 DOM 을 제거할지 여부
     */
    async remove(removeDom: boolean = true): Promise<void> {
        if (this.menu !== null) {
            this.menu.remove();
        }

        super.remove(removeDom);
    }
}
